import React, { useState, useCallback, useRef, useLayoutEffect, useContext, useMemo, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Collapse,
    Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { InboxControlsState, InboxControlsProps } from "../../../types/ruleBasedInbox";
import { LAST_ACTIVITY_OPTIONS, THREAD_CHANNEL_NAMES } from "../constants";
import GlobalTooltip from "../../../components/utility/tooltip/globalTooltip";
import styles from "../../../assets/styles/pageStyles/ruleBasedInbox/inboxControls/inboxControls.module.scss";
import globalStyles from "../../../assets/styles/global.module.scss";
import threadListStyles from "../../../assets/styles/pageStyles/ruleBasedInbox/threadList/threadList.module.scss";
import GlobalButton from "../../../components/utility/globalButton/button";
import { useShallow } from "zustand/react/shallow";
import { useTicketStore } from "../../../store/ticketStore";
import { THREAD_STATUS } from "../threadInfo/components/threadStatus/customerConversationAssignedStatus";
import { UserContext, UserContextPayload } from "../../../context/userContext";
import { CHRONOX_FEATURES, getIntegrationIcon, getUserFullName, isFeatureDisabled } from "../../../utils/constants";
import { integratedChannel, organizationUserDataModel } from "../../../models/axiosModel";
import SearchField from "./searchField";
import InboxFilterAutocomplete from "./inboxFilterAutocomplete";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";
import clsx from "clsx";
import RefreshIcon from '@mui/icons-material/Refresh';
import { getDefaultInboxControlsState, getIntegratedChannelOptionText, getUserAgentOptions } from "../utils";
import { threadTicketDisableFeatureTooltipWrapper } from "../renderUtils";

/**
 * Inbox Controls Component
 * Filter panel for inbox conversations
 */
const InboxControls: React.FC<InboxControlsProps> = ({
    onClose
}) => {

    const { ticketStatusData, filterStatusesBySubstatusFlag } = useTicketStore(
        useShallow((state) => ({
            ticketStatusData: state.ticketStatusData,
            filterStatusesBySubstatusFlag: state.filterStatusesBySubstatusFlag
        }))
    )

    const { userInfoSearchPhrase, setUserInfoSearchPhrase, ticketInfoSearchPhrase, setTicketInfoSearchPhrase, getAllIntegratedChannelsApi, 
        integratedChannels, setIntegratedChannels, setFilters, threadTagsData, filters
    } = useRuleBasedInboxStore(useShallow((state) => ({
        userInfoSearchPhrase: state.userInfoSearchPhrase, 
        setUserInfoSearchPhrase: state.setUserInfoSearchPhrase,
        ticketInfoSearchPhrase: state.ticketInfoSearchPhrase,
        setTicketInfoSearchPhrase: state.setTicketInfoSearchPhrase,
        getAllIntegratedChannelsApi: state.getAllIntegratedChannelsApi,
        integratedChannels: state.integratedChannels, 
        setIntegratedChannels: state.setIntegratedChannels,
        setFilters: state.setFilters,
        threadTagsData: state.threadTagsData,
        filters: state.filters
    })))

    const { userDetail, vendorData, organizationDetails, userAssignmentRoleData, setFlagLoading, handleError, getUserAssignmentRoleApi } = useContext(UserContext) as UserContextPayload

    const [localFilters, setLocalFilters] = useState<InboxControlsState>(filters);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);

    // Ref to preserve scroll position
    const contentRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef<number>(0);

    // Check if thread ticketing feature is disabled.
    const isThreadTicketingFeatureDisabled = useMemo(() => {
        return isFeatureDisabled(userDetail, CHRONOX_FEATURES['THREAD_TICKETING']);
    }, [userDetail]);    

    const getUserAssignmentRole = async () => {
        const res = await getUserAssignmentRoleApi();
        if(!(res.status)){
            handleError(res);
        } 
    } 

    // Get All Integrated Channels Call.
    const getAllIntegratedChannelsApiCall = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await getAllIntegratedChannelsApi();
        if(res){
            if(!(res.status)){
                handleError(res);
            } else {
                setIntegratedChannels((res?.data as integratedChannel[])?.filter((channel) => channel?.channel_name?.toLowerCase() !== THREAD_CHANNEL_NAMES['CHRONOX_AI_WEB_APP']?.toLowerCase()));
            }
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    useEffect(() => {
        if(!userAssignmentRoleData && !isThreadTicketingFeatureDisabled){
            getUserAssignmentRole();
        }

        if(!integratedChannels){
            getAllIntegratedChannelsApiCall(); // Get All Integrated Channels Call.
        }
    }, []);

    // Save scroll position before state update
    const saveScrollPosition = useCallback(() => {
        if (contentRef.current) {
            scrollPositionRef.current = contentRef.current.scrollTop;
        }
    }, []);

    // Restore scroll position after state update
    useLayoutEffect(() => {
        if (contentRef.current && scrollPositionRef.current > 0) {
            contentRef.current.scrollTop = scrollPositionRef.current;
        }
    }, [localFilters]);

    // Update local filters when props change
    useEffect(() => {
        saveScrollPosition();
        // setLocalFilters(filters);
        const ticketCreatedBy = filters?.ticketCreatedBy; 
        setLocalFilters({
            ...filters,
            ticketCreatedByType: ticketCreatedBy?.length == 0 ? 'All' : (ticketCreatedBy?.length == 1 && ticketCreatedBy?.[0] == userDetail?.user_assignment_id) ? 'Created by Me' : 'Created by Others'
        })
    }, [filters, saveScrollPosition]);

    // Common handler for radio button filter changes
    const handleRadioFilterChange = useCallback(
        (fieldOrUpdates: keyof InboxControlsState | ((value: string) => Partial<InboxControlsState>)) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            const updates =
                typeof fieldOrUpdates === "function"
                    ? fieldOrUpdates(value)
                    : ({ [fieldOrUpdates]: value } as Partial<InboxControlsState>);

            setLocalFilters((prev) => ({
                ...prev,
                ...updates,
            }));
        },
        []
    );

    const getTicketCreatorFilterUpdates = useCallback(
        (value: string): Partial<InboxControlsState> => {
            const currentUserId = userDetail?.user_assignment_id;

            if (value === "All") {
                return { ticketCreatedByType: value, ticketCreatedBy: [] };
            }
            if (value === "Created by Me") {
                return {
                    ticketCreatedByType: value,
                    ticketCreatedBy: currentUserId != null ? [currentUserId] : [],
                };
            }
            return {
                ticketCreatedByType: "Created by Others",
                ticketCreatedBy: [],
            };
        },
        [userDetail?.user_assignment_id]
    );

    // Common handler for checkbox filter changes
    const handleCheckboxFilterChange = useCallback(
        (
            fieldOrUpdates:
                | keyof InboxControlsState
                | ((checked: boolean, prev: InboxControlsState) => Partial<InboxControlsState>)
        ) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const checked = event.target.checked;
            setLocalFilters((prev) => {
                const updates =
                    typeof fieldOrUpdates === "function"
                        ? fieldOrUpdates(checked, prev)
                        : ({ [fieldOrUpdates]: checked } as Partial<InboxControlsState>);
                return { ...prev, ...updates };
            });
        },
        []
    );

    const getTicketAssignedToMeUpdates = useCallback(
        (checked: boolean, prev: InboxControlsState): Partial<InboxControlsState> => {
            const currentUserId = userDetail?.user_assignment_id;
            return {
                ticketAssignedTo: checked
                    ? [...(prev.ticketAssignedTo ?? []), currentUserId]
                    : prev.ticketAssignedTo?.filter((userAssignmentId) => userAssignmentId != currentUserId),
            };
        },
        [userDetail?.user_assignment_id]
    );

    const getTicketStatusToggleUpdates = useCallback(
        (statusId: string) =>
        (_checked: boolean, prev: InboxControlsState): Partial<InboxControlsState> => {
            const currentStatusIds = prev.ticketStatusIds || [];
            return {
                ticketStatusIds: currentStatusIds.includes(statusId)
                    ? currentStatusIds.filter((id) => id !== statusId)
                    : [...currentStatusIds, statusId],
            };
        },
        []
    );

    const getTagToggleUpdates = useCallback(
        (tagId: string) =>
        (_checked: boolean, prev: InboxControlsState): Partial<InboxControlsState> => {
            const currentTags = prev.tags || [];
            return {
                tags: currentTags.includes(tagId)
                    ? currentTags.filter((id) => id !== tagId)
                    : [...currentTags, tagId],
            };
        },
        []
    );

    const handleTicketAssignmentToOther = useCallback(
        (_: React.SyntheticEvent, value: organizationUserDataModel[]) => {
            const userAssignmentIds = (value ?? [])?.map((user) => user?.user_assignment_id);
            const ticketAssignedToMe = localFilters.ticketAssignedTo?.find((assignedId) => assignedId == userDetail?.user_assignment_id);
            setLocalFilters({
                ...localFilters,
                flagTicketAssignedToOther: userAssignmentIds?.length > 0 ? false : true,
                ticketAssignedTo: ticketAssignedToMe ? [userDetail?.user_assignment_id, ...userAssignmentIds]: userAssignmentIds
            });
        },  
        [localFilters]
    )

    // Handle Ticket Created By autocomplete change (when "Created by Others" is selected)
    const handleTicketCreatedByChange = useCallback(
        (_: React.SyntheticEvent, value: organizationUserDataModel[]) => {
            const userAssignmentIds = (value ?? [])?.map((user) => user?.user_assignment_id);
            setLocalFilters({
                ...localFilters,
                ticketCreatedBy: userAssignmentIds,
            });
        },
        [localFilters]
    );

    // Common handler for search phrase input changes (user info / ticket info)
    const handleSearchPhraseChange = useCallback(
        (setSearchPhrase: (value: string) => void) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchPhrase(event?.target?.value);
        },
        []
    );

    // Common handler for search filter changes (user info / ticket info)
    const onSearchFilterChange = useCallback(
        (field: "searchQuery" | "ticketSearchQuery") =>
        (value: string[]) => {
            setLocalFilters((prev) => ({
                ...prev,
                [field]: value.join(","),
            }));
        },
        []
    );

    // Handle Integrated Channel change
    const handleIntegratedChannelChange = useCallback(
        (_: React.SyntheticEvent, value: integratedChannel[]) => {
            const integratedChannelIds = (value ?? [])?.map((channel) => String(channel?.integrated_channel_id));
            setLocalFilters({
                ...localFilters,
                integratedChannelId: integratedChannelIds,
            });
        },
        [localFilters]
    );

    // Handle apply
    const handleApply = useCallback(() => {
        setFilters(localFilters);
        onClose();
    }, [localFilters]);

    // Handle reset
    const handleReset = useCallback(() => {
        setFilters(getDefaultInboxControlsState(userDetail))
        onClose();
    }, []);

    const getAgentOptions = useCallback(() => {
        return getUserAgentOptions(organizationDetails, userAssignmentRoleData);
    }, [organizationDetails, userAssignmentRoleData]) 

    const getTicketAssignedToOther = useMemo(() => {
        return localFilters.ticketAssignedTo?.filter((user) => user != userDetail?.user_assignment_id); 
    }, [localFilters.ticketAssignedTo])

    const getIntegratedChannelOptionLabel = (option: integratedChannel) => {
        const text = getIntegratedChannelOptionText(option);
        if (!text) return "";
        return (
            <Grid container className={clsx(globalStyles.displayFlex, globalStyles.gapOne, styles.integratedChannelChipLabel)} flexWrap={"nowrap"}>
                <Grid item className={clsx(globalStyles.displayFlexStyle, styles.integratedChannelChipIcon)}>
                    {getIntegrationIcon(option.channel_name, "16", threadListStyles.whatsappIcon)}
                </Grid>
                <Grid item xs zeroMinWidth className={styles.integratedChannelChipText} title={text}>
                    {text}
                </Grid>
            </Grid>
        );
    };

    const renderFilterSectionHeader = (
        sectionTitleName: string,
        tooltipTitle: string,
        options?: {
            titleClassName?: string;
            isTicketFeatureGated?: boolean;
        }
    ) => {
        const titleClassName = options?.titleClassName ?? styles.subSectionTitle;
        const isTicketFeatureGated = options?.isTicketFeatureGated ?? false;
        const titleNode = <Typography className={titleClassName}>{sectionTitleName}</Typography>;

        return (
            <Box
                className={clsx(
                    styles.sectionHeader,
                    isTicketFeatureGated && isThreadTicketingFeatureDisabled && styles.disableSectionHeader
                )}
            >
                {isTicketFeatureGated
                    ? threadTicketDisableFeatureTooltipWrapper(userDetail, "right", titleNode, vendorData)
                    : titleNode}
                <GlobalTooltip
                    title={tooltipTitle}
                    placement="top"
                    arrow
                    disableHoverListener={isTicketFeatureGated ? isThreadTicketingFeatureDisabled : undefined}
                >
                    <HelpOutlineIcon className={styles.helpIcon} />
                </GlobalTooltip>
            </Box>
        );
    };

    const renderFormGroup = (children: React.ReactNode, className: string = styles.checkboxGroup) => {
        return <FormGroup className={className}>{children}</FormGroup>;
    };

    const renderRadioGroup = (value: string, onChange: (event: React.ChangeEvent<HTMLInputElement>) => void, children: React.ReactNode, className: string = styles.radioGroup) => {
        return (
            <RadioGroup value={value} onChange={onChange} className={className}>
                {children}
            </RadioGroup>
        );
    };

    const renderFormControlLabel = ({ label, controlType = "checkbox", checked, onChange, value, disabled, className, key } : {
        label: React.ReactNode;
        controlType?: "checkbox" | "radio";
        checked?: boolean;
        onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
        value?: string;
        disabled?: boolean;
        className?: string;
        key?: React.Key;
    }) => {
        const isRadio = controlType === "radio";

        return (
            <FormControlLabel
                key={key}
                value={value}
                control={
                    isRadio ? (
                        <Radio size="small" disableRipple />
                    ) : (
                        <Checkbox
                            size="small"
                            disableRipple
                            checked={checked}
                            onChange={onChange}
                            disabled={disabled}
                        />
                    )
                }
                label={label}
                className={className ?? (isRadio ? styles.radioLabel : styles.checkboxLabel)}
                disabled={disabled}
            />
        );
    };

    return (
        <Box className={styles.inboxControlsContainer}>
            {/* Header */}
            <Box className={styles.header}>
                <Typography className={styles.title}>Inbox Controls</Typography>
                <IconButton
                    size="small"
                    onClick={onClose}
                    className={styles.closeButton}
                    aria-label="Close"
                > 
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box ref={contentRef} className={styles.content}>
                {/* Conversations Section */}
                <Box className={styles.section}>
                    {renderFilterSectionHeader(
                        "Conversations",
                        "Choose which type of conversations you want to view in the inbox. Select Active conversations or All conversations.",
                        { titleClassName: styles.sectionTitle }
                    )}
                    {renderRadioGroup(
                        localFilters.conversationType,
                        handleRadioFilterChange("conversationType"),
                        <>
                            {renderFormControlLabel({
                                controlType: "radio",
                                value: "Active",
                                label: (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                        <span>Active</span>
                                        <GlobalTooltip
                                            title="Displays conversations that are currently ongoing or have had recent activity. A conversation is considered active if it's not archived AND either: assigned to an agent, has activity within the past 72 hours, or has an Open ticket status."
                                            placement="top"
                                            arrow
                                        >
                                            <HelpOutlineIcon sx={{ fontSize: 14, color: "#9e9e9e", cursor: "pointer" }} />
                                        </GlobalTooltip>
                                    </Box>
                                ),
                            })}
                            {renderFormControlLabel({
                                controlType: "radio",
                                value: "All",
                                label: "All",
                            })}
                        </>
                    )}
                </Box>

                {/* Advanced Options Section */}
                <Box className={styles.section}>
                    <Box
                        className={styles.sectionHeader}
                        onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                        sx={{ cursor: "pointer" }}
                    >
                        <Typography className={styles.sectionTitle}>Advanced Options</Typography>
                        {isAdvancedOpen ? (
                            <ExpandLessIcon className={styles.expandIcon} />
                        ) : (
                            <ExpandMoreIcon className={styles.expandIcon} />
                        )}
                    </Box>

                    <Collapse in={isAdvancedOpen}>
                        {/* Assignment Scope - Checkboxes */}
                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Assignment Scope",
                                "Filter conversations based on assignment. You can choose conversations that are Unassigned, Assigned to Me, or both."
                            )}
                            {renderFormGroup(
                                <>
                                    {renderFormControlLabel({
                                        label: "Unassigned",
                                        checked: localFilters.flagUnassigned,
                                        onChange: handleCheckboxFilterChange("flagUnassigned"),
                                    })}
                                    {renderFormControlLabel({
                                        label: "Assigned to Me",
                                        checked: localFilters.flagAssignedToMe,
                                        onChange: handleCheckboxFilterChange("flagAssignedToMe"),
                                    })}
                                </>
                            )}
                        </Box>

                        {/* Conversation Type - Only visible when "All" is selected */}
                        <Collapse in={localFilters.conversationType === "All"} timeout={150}>
                            <Box
                                className={styles.subSection}
                            >
                                {renderFilterSectionHeader(
                                    "Conversation Type",
                                    "Filter conversations by their type. This can include Archived or Unarchived conversations, based on whether they are active or have been resolved and stored for later reference."
                                )}
                                {renderFormGroup(
                                    <>
                                        {renderFormControlLabel({
                                            label: (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                    <span>Archived</span>
                                                    <GlobalTooltip
                                                        title="You can only filter by Archived conversations under the 'All' section, as archived chats are not part of active conversations."
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <HelpOutlineIcon sx={{ fontSize: 14, color: "#9e9e9e", cursor: "pointer" }} />
                                                    </GlobalTooltip>
                                                </Box>
                                            ),
                                            checked: localFilters.flagArchived,
                                            onChange: handleCheckboxFilterChange("flagArchived"),
                                        })}
                                        {renderFormControlLabel({
                                            label: "Unarchived",
                                            checked: localFilters.flagUnarchived,
                                            onChange: handleCheckboxFilterChange("flagUnarchived"),
                                        })}
                                    </>
                                )}
                            </Box>
                        </Collapse>

                        {/* Ticket Status - From API */}
                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Integrated Channel",
                                "Filter conversations by integrated channel"
                            )}

                            <InboxFilterAutocomplete
                                options={integratedChannels ?? []}
                                value={integratedChannels?.filter((channel) => localFilters?.integratedChannelId?.some((channelId) => String(channelId) === String(channel?.integrated_channel_id))) ?? []}
                                getOptionLabel={(option) => getIntegratedChannelOptionText(option)}
                                renderOptionLabel={(option) => getIntegratedChannelOptionLabel(option)}
                                isOptionEqualToValue={(option, selectedOption) =>
                                    option?.integrated_channel_id === selectedOption?.integrated_channel_id
                                }
                                getOptionKey={(option) => option?.integrated_channel_id}
                                onChange={handleIntegratedChannelChange}
                                placeholder="Select Integrated Channels"
                                selectedCountLabel="channel"
                                chipClassName={styles.integratedChannelChip}
                                suffixAction={
                                    <GlobalTooltip
                                        placement="top"
                                        title="Fetch latest integrated channels"
                                        arrow
                                    >
                                        <IconButton
                                            size="small"
                                            className={styles.integratedChannelRefreshButton}
                                            onClick={getAllIntegratedChannelsApiCall}
                                        >
                                            <RefreshIcon
                                                fontSize="small"
                                                className={globalStyles.primaryColor}
                                            />
                                        </IconButton>
                                    </GlobalTooltip>
                                }
                            />
                        </Box>

                        {/* Ticket Status - From API */}
                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Ticket State",
                                "Filter conversations by their associated ticket status. Resolved tickets are only available in All Chats mode.",
                                { isTicketFeatureGated: true }
                            )}
                            {renderFormGroup(
                                ticketStatusData && ticketStatusData.length > 0 && filterStatusesBySubstatusFlag(false)?.length > 0 ? (
                                    filterStatusesBySubstatusFlag(false)?.map((status) =>
                                        renderFormControlLabel({
                                            key: status.ticket_status_id,
                                            label: status.ticket_status_name,
                                            checked: localFilters.ticketStatusIds?.includes(status.ticket_status_id),
                                            onChange: handleCheckboxFilterChange(getTicketStatusToggleUpdates(status.ticket_status_id)),
                                            disabled: ((localFilters.conversationType === 'Active' && status?.ticket_status_name?.toLowerCase() === THREAD_STATUS['RESOLVED']?.toLowerCase()) || isThreadTicketingFeatureDisabled),
                                        })
                                    )
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#9e9e9e", fontSize: "13px" }}>
                                        No status options available
                                    </Typography>
                                )
                            )}
                        </Box>

                        {/* Ticket Sub-Status - From API */}
                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Ticket Sub-status",
                                "Filter conversations by ticket sub-statues. Sub-statues provide additional context about ticket progress and state.",
                                { isTicketFeatureGated: true }
                            )}
                            {renderFormGroup(
                                ticketStatusData && ticketStatusData.length > 0 && filterStatusesBySubstatusFlag(true)?.length > 0 ? (
                                    filterStatusesBySubstatusFlag(true)?.map((status) =>
                                        renderFormControlLabel({
                                            key: status.ticket_status_id,
                                            label: (
                                                <>
                                                    <Grid container gap={1} className={globalStyles.displayFlex}>
                                                        <Grid item className={styles.ticketSubStatusBox} sx={{ background: `${status?.ticket_status_color}` }}></Grid>
                                                        <Grid item>
                                                            {status?.ticket_status_name}
                                                        </Grid>
                                                    </Grid>
                                                </>
                                            ),
                                            checked: localFilters.ticketStatusIds?.includes(status.ticket_status_id),
                                            onChange: handleCheckboxFilterChange(getTicketStatusToggleUpdates(status.ticket_status_id)),
                                            disabled: isThreadTicketingFeatureDisabled,
                                        })
                                    )
                                ) : (
                                    <Typography variant="body2" sx={{ color: "#9e9e9e", fontSize: "13px" }}>
                                        No substatus options available
                                    </Typography>
                                ),
                                styles.tagsContainer
                            )}
                        </Box>

                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Ticket Creator",
                                "Filter by who created the ticket, useful for finding tickets you've created or tracking tickets created by other agents.",
                                { isTicketFeatureGated: true }
                            )}
                            {renderRadioGroup(
                                localFilters.ticketCreatedByType,
                                handleRadioFilterChange(getTicketCreatorFilterUpdates),
                                <>
                                    {renderFormControlLabel({
                                        controlType: "radio",
                                        value: "All",
                                        label: "All",
                                        disabled: isThreadTicketingFeatureDisabled,
                                    })}
                                    {renderFormControlLabel({
                                        controlType: "radio",
                                        value: "Created by Me",
                                        label: "Created by Me",
                                        disabled: isThreadTicketingFeatureDisabled,
                                    })}
                                    {renderFormControlLabel({
                                        controlType: "radio",
                                        value: "Created by Others",
                                        label: "Created by Others",
                                        disabled: isThreadTicketingFeatureDisabled,
                                    })}
                                </>
                            )}
                            {/* {localFilters?.ticketCreatedBy?.length > 0 && !(localFilters.ticketCreatedBy?.length == 1 && localFilters.ticketCreatedBy[0] === userDetail?.user_assignment_id) &&  */}
                            {localFilters?.ticketCreatedByType == "Created by Others" &&
                                <Grid item xs={12} mt={2}>
                                    <Grid container className={`${globalStyles.displayFlexStyle} ${globalStyles.flexColumn}`} gap={1}>
                                        <Grid item>
                                            <Typography className={styles.ticketCreatedByTitle}>
                                                Ticket Created By
                                            </Typography>
                                        </Grid>
                                        <InboxFilterAutocomplete
                                            options={organizationDetails?.[0]?.organization_user_data?.filter((user) => user?.user_assignment_id != userDetail?.user_assignment_id) || []}
                                            value={organizationDetails?.[0]?.organization_user_data?.filter((user) => localFilters.ticketCreatedBy?.some((userAssignmentId) => userAssignmentId == user?.user_assignment_id)) ?? []}
                                            getOptionLabel={(option) => getUserFullName(option)}
                                            isOptionEqualToValue={(option, selectedOption) =>
                                                option?.user_assignment_id === selectedOption?.user_assignment_id
                                            }
                                            getOptionKey={(option) => option?.user_assignment_id}
                                            onChange={handleTicketCreatedByChange}
                                            disabled={isThreadTicketingFeatureDisabled}
                                            placeholder="Created By"
                                            selectedCountLabel="user"
                                        />
                                    </Grid>
                                </Grid>
                            }
                        </Box>

                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Ticket Assignment",
                                "Filter by ticket assignment status. Different from conversation assignment, this filters based on who the ticket itself is assigned to.",
                                { isTicketFeatureGated: true }
                            )}
                            {renderFormGroup(
                                <>
                                    {renderFormControlLabel({
                                        label: "Assigned to Me",
                                        checked: localFilters.ticketAssignedTo?.find((userAssignmentId) => userAssignmentId == userDetail?.user_assignment_id) ? true : false,
                                        onChange: handleCheckboxFilterChange(getTicketAssignedToMeUpdates),
                                        disabled: isThreadTicketingFeatureDisabled,
                                    })}
                                    {renderFormControlLabel({
                                        label: "Unassigned",
                                        checked: localFilters.flagTicketUnassigned,
                                        onChange: handleCheckboxFilterChange("flagTicketUnassigned"),
                                        disabled: isThreadTicketingFeatureDisabled,
                                    })}
                                    {renderFormControlLabel({
                                        label: "Assigned to Other",
                                        checked: localFilters.flagTicketAssignedToOther || getTicketAssignedToOther?.length > 0,
                                        onChange: handleCheckboxFilterChange("flagTicketAssignedToOther"),
                                        disabled: isThreadTicketingFeatureDisabled,
                                    })}
                                </>
                            )}

                            {(localFilters.flagTicketAssignedToOther || getTicketAssignedToOther?.length > 0) && 
                                <Grid item xs={12} mt={2}>
                                    <Grid container className={`${globalStyles.displayFlexStyle} ${globalStyles.flexColumn}`} gap={1}>
                                        <Grid item>
                                            <Typography className={styles.ticketCreatedByTitle}>
                                                Ticket Assigned to
                                            </Typography>
                                        </Grid>
                                        <InboxFilterAutocomplete
                                            options={getAgentOptions()?.filter((user: organizationUserDataModel) => user?.user_assignment_id != userDetail?.user_assignment_id) || []}
                                            value={organizationDetails?.[0]?.organization_user_data?.filter((user) =>localFilters.ticketAssignedTo?.some((userAssignmentId) => userAssignmentId == user?.user_assignment_id && userAssignmentId != userDetail?.user_assignment_id)) ?? []}
                                            getOptionLabel={(option) => getUserFullName(option)}
                                            isOptionEqualToValue={(option, selectedOption) =>
                                                option?.user_assignment_id === selectedOption?.user_assignment_id
                                            }
                                            getOptionKey={(option) => option?.user_assignment_id}
                                            onChange={handleTicketAssignmentToOther}
                                            disabled={isThreadTicketingFeatureDisabled}
                                            placeholder="Assigned to"
                                            selectedCountLabel="user"
                                        />
                                    </Grid>
                                </Grid>
                            }
                        </Box>

                        {/* Tags - From API */}
                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Tags",
                                "Filter conversations based on Tags. Tags are labels that you or your team can create and assign to conversations for better organization. Use tags to categorize and easily find conversations."
                            )}
                            <Box className={styles.tagsContainer}>
                                {renderFormGroup(
                                    threadTagsData && threadTagsData.length > 0 ? (
                                        threadTagsData.map((tag) =>
                                            renderFormControlLabel({
                                                key: tag.ticket_tag_id,
                                                label: tag.ticket_tag_name,
                                                checked: localFilters.tags?.includes(tag.ticket_tag_id),
                                                onChange: handleCheckboxFilterChange(getTagToggleUpdates(tag.ticket_tag_id)),
                                            })
                                        )
                                    ) : (
                                        <Typography variant="body2" sx={{ color: "#9e9e9e", fontSize: "13px" }}>
                                            No tags available
                                        </Typography>
                                    )
                                )}
                            </Box>
                        </Box>

                        {/* Last Activity */}
                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Last Activity",
                                "Filter conversations based on the last time activity occurred. You can choose to view all conversations or filter based on activity within the last 24 hours, 72 hours, 7 days, or all-time."
                            )}
                            {renderRadioGroup(
                                localFilters.lastActivity,
                                handleRadioFilterChange("lastActivity"),
                                LAST_ACTIVITY_OPTIONS.map((option) =>
                                    renderFormControlLabel({
                                        key: option.value,
                                        controlType: "radio",
                                        value: option.value,
                                        label: option.label,
                                    })
                                )
                            )}
                        </Box>

                        {/* Search */}
                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Search by user info",
                                "Search conversations by user name, user phone number and user metadata."
                            )}
                            <SearchField
                                value={userInfoSearchPhrase}
                                searchQuery={localFilters.searchQuery}
                                handleSearchChange={handleSearchPhraseChange(setUserInfoSearchPhrase)}
                                resetSearchValue={() => setUserInfoSearchPhrase('')}
                                onSearch={onSearchFilterChange("searchQuery")}
                            />
                        </Box>

                        <Box className={styles.subSection}>
                            {renderFilterSectionHeader(
                                "Search by ticket info",
                                "Search conversations by ticket reference number, ticket title, ticket description, ticket notes and ticket comments.",
                                { isTicketFeatureGated: true }
                            )}
                            <SearchField
                                value={ticketInfoSearchPhrase}
                                searchQuery={localFilters.ticketSearchQuery}
                                handleSearchChange={handleSearchPhraseChange(setTicketInfoSearchPhrase)}
                                resetSearchValue={() => setTicketInfoSearchPhrase('')}
                                onSearch={onSearchFilterChange("ticketSearchQuery")}
                                isDisable={isThreadTicketingFeatureDisabled}
                            />
                        </Box>
                    </Collapse>
                </Box>
            </Box>

            {/* Footer Actions */}
            <Box className={styles.footer}>
                <Box
                    className={styles.resetButton}
                    onClick={handleReset}
                    role="button"
                    tabIndex={0}
                >
                    <Typography>Reset</Typography>
                </Box>

                <GlobalButton sx={{ width: "50%" }} buttonText="Apply" onClick={handleApply} className="primaryButtonStyle" />
            </Box>
        </Box>
    );
};

export default InboxControls;
